/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Attributes from "model/stats/Attributes";
import Monster from "model/monster/Monster";


import { base_actions } from "./abilityData/baseActions";
import { traits } from "./abilityData/traits";
import { reactions } from "./abilityData/reactions";
import { bonus_actions } from "./abilityData/bonusActions";
import { legendary_actions } from "./abilityData/legendaryActions";
import { weapons } from "./abilityData/weapons";

class Abilities {
  static base_actions: Ability[] = base_actions;
  static traits = traits;
  static reactions: Ability[] = reactions;
  static bonus_actions: Ability[] = bonus_actions;
  static legendary_actions: Ability[] = legendary_actions;
  static weapons: Ability[] = weapons;

	static getGearNames(): string[] {
        return [...new Set(this.weapons.map(weapon => weapon.name))];
    }
	static getMagicGearNames(): string[] {
        return [...new Set(this.magic_items.map(item => item.name))];
    }

	static actions = [...this.base_actions, ...this.weapons];

	static magic_items: Ability[] = [];
  
	static getOptions(blockType: string): Ability[] {
	  if (blockType === 'actions')           return this.actions;
	  if (blockType === 'legendary_actions') return this.legendary_actions;
	  if (blockType === 'bonus_actions')     return this.bonus_actions;
	  if (blockType === 'reactions')         return this.reactions;
	  if (blockType === 'traits')            return this.traits;
	  if (blockType === 'inventory')         return this.magic_items;
	  return [];
	}

	static setMagicItems(magicItems: any[], documents: Map<string, { name: string; licenses: string[]; }>): void {
		const docMap = new Map<string, { name: string; order: number }>();
		let index = 0;
		documents.forEach((licenses, key, map) => {
		  docMap.set(key, { name: licenses.name, order: index++ });
		});
	
		magicItems.forEach(mi => {
		  const doc = docMap.get(mi.document__slug);
		  if (doc) {
			mi.label = mi.name;
			mi.group = doc.name;
			mi.groupOrder = doc.order;
		  }
		});
	
		this.magic_items = magicItems.sort((m1: Ability, m2: Ability) => {
		  if (m1.groupOrder! < m2.groupOrder!) return -1;
		  if (m1.groupOrder! > m2.groupOrder!) return 1;
		  return (m1.label ?? m1.name).localeCompare(m2.label ?? m2.name);
		});
	  }

	/*
      [MON]: Shows the monster's name.
      [MONS]: Shows the monster's plural name.
      [CHA]: Shows the monster's charisma modifier.
      [PB]: Shows the monster's proficiency bonus.
      [3D6]: Computes 3d6.
      [STR ATK]: Calculates the modifier to the monster's attack roll for a strength-based attack.
      [DEX 2D8]: Calculates the damage roll for a dexterity-based attack with damage dice 2d8.
      [WIS SAVE]: Calculates the save DC vs the monster's wisdom.
      [3D6 + 1], [STR ATK - 2], [WIS SAVE + 3], [PB + 2]: Adds a modifier to the given values.
    */
static ReplaceTags(desc: string, mon: Monster): string {
    const bracketExp = /\[(.*?)\]/g;
    const matches: RegExpExecArray[] = [];
    let match;

    while ((match = bracketExp.exec(desc)) != null) {
      matches.push(match);
    }

	// We use reduceRight because by starting from the last match and moving towards the first,
	// we ensure that replacing a match does not affect the indices of the remaining matches.
	// This is crucial because string replacement can alter the length of the string, 
	// which could invalidate the indices of subsequent matches if they were calculated beforehand.
	return matches.reduceRight((currentDesc, match) => {
      let readString = Abilities.parseReadString(match[1]);
      if (!readString) {
        return currentDesc;
      }
      if (readString === "mon") {
        return Abilities.replaceMatch(currentDesc, match, (mon.shortName || mon.name).toLowerCase());
      }
      if (readString === "mons") {
        return Abilities.replaceMatch(currentDesc, match, (mon.pluralName || mon.name).toLowerCase());
      }
	  const capitalize = (str: string): string => {
		return str[0].toUpperCase() + str.substring(1).toLowerCase();
	  }
      if (readString === "Mon") {
        return Abilities.replaceMatch(currentDesc, match, capitalize(mon.shortName || mon.name));
      }
      if (readString === "Mons") {
        return Abilities.replaceMatch(currentDesc, match, capitalize(mon.pluralName || mon.name));
      }
	  readString = readString.toLowerCase();
      if (readString === "mon") {
        return Abilities.replaceMatch(currentDesc, match, (mon.shortName || mon.name));
      }
      if (readString === "mons") {
        return Abilities.replaceMatch(currentDesc, match, (mon.pluralName || mon.name));
      }
	  //if (readString === "pb") {
      //  return Abilities.replaceMatch(currentDesc, match, mon.proficiencyBonus);
      //}
      return Abilities.processCustomDescriptor(currentDesc, match, readString, mon);
    }, desc);
  }

  static parseReadString(str: string): string {
    return str.replace(/ +/g, ' ').trim();
  }

  static replaceMatch(desc: string, match: RegExpExecArray, replacement: string): string {
    return desc.slice(0, match.index) + replacement + desc.slice(match.index + match[0].length);
  }

  static GetAttributeBonus(stat: string, mon: Monster): number | null {
	const attrName = Attributes.getAttrName(stat);
	if (attrName != '') {
		const attrLevel = mon.attributes[attrName]; // Assert the type
		return Attributes.getBonus(attrLevel);
	}
	return null;
  }
  
  static processCustomDescriptor(desc: string, match: RegExpExecArray, readString: string, mon: Monster): string {
    const damageExp = /(\d*)d(\d+)/;
    const bonusExp = /^[+-] ?(\d+)$/;

    if (readString.length == 0) {
      return desc;
    }

    let readPosition = 0;
    let type: string | null = null;
    let attrBonus: number | null = 0;
	if (readString.startsWith('pb')) {
		attrBonus = mon.proficiencyBonus;
		type = 'pb';
		readPosition = 2;
	} else if (readString.startsWith('prof')) {
		attrBonus = mon.proficiencyBonus;
		type = 'pb';
		readPosition = 4;
	} else {
		attrBonus = Abilities.GetAttributeBonus(readString.substring(0, 3), mon);
	}
    let bonus = 0;

    if (attrBonus != null && !isNaN(attrBonus)) {
      bonus = attrBonus;
      readPosition = readPosition || 3;
      type = type || 'stat';
      if ((readString.length > 3) && (readString !== 'pb')) {
        if (readString.substring(3, 7).toLowerCase() === ' atk') {
          bonus += mon.proficiencyBonus;
          readPosition = 7;
          type = 'atk';
        } else if (readString.substring(3, 8).toLowerCase() === ' save') {
          bonus += mon.proficiencyBonus + 8;
          readPosition = 8;
          type = 'save';
        }
      }

      if (readPosition < readString.length) {
        while (readString[readPosition] === ' ') readPosition++;
      }
    }

    let rollDice: number | null = null;
    let rollDieType: number | null = null;
    if ((type == null || type === 'stat' || type === 'pb') && readPosition < readString.length) {
      const nextToken = readString.substring(readPosition).replace(/ +/g, '');

      const damageMatch = damageExp.exec(nextToken);
      if (damageMatch != null) {
        rollDice = damageMatch[1] ? parseInt(damageMatch[1]) : 1;
        rollDieType = parseInt(damageMatch[2]);
        readPosition += damageMatch[0].length;
        type = 'dmg';

        if ((readPosition < readString.length) && (readString[readPosition] === ' ')) {
          readPosition++;
        }
      } else {
		try {
			const tokenInt = parseInt(nextToken.trim());
			if (nextToken.trim() === ('' + tokenInt)) {
				// this is a pure number, like [str 1] for an unarmed strike:
				rollDieType = 1;
				readPosition += nextToken.length;
				type = 'dmg';
			}
		} catch (error) {
			console.log('error parsing int ' + nextToken);
			type = 'error';
		}
	  }
    }

    if (type != 'error' && readPosition < readString.length) {
      const nextToken = readString.substring(readPosition);
      const bonusMatch = nextToken.match(bonusExp);
      if (bonusMatch) {
        bonus += nextToken[0] === '-' ? -parseInt(bonusMatch[1]) : parseInt(bonusMatch[1]);
      } else {
        type = 'error';
      }
    }

    if (type == null || type === 'error') {
      return desc;
    }

    let results: string = desc;
    switch (type) {
	  case 'pb':
		  results = '' + bonus;
		  break;
      case 'stat':
      case 'atk':
        results = ((bonus >= 0) ? '+': '') + bonus;
        break;
      case 'save':
        results = bonus.toString();
        break;
      case 'dmg':
		if (rollDieType === 1) {
			results = ((rollDice || 1) + bonus) + ' (' + (rollDice || 1);
		} else {
			results = Math.max(Math.floor((rollDice || 1) * ((rollDieType! + 1) / 2) + bonus), 1) +
				' (' + (rollDice || 1) + 'd' + rollDieType;
		}
		results += ((bonus > 0) ? ' + ' + bonus : 
				   ((bonus < 0) ? ' - ' + -bonus : '')) + ')';
        break;
    }
    return Abilities.replaceMatch(desc, match, results);
  }
}

export default Abilities;
